import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SideNav } from './SideNav.js';
import { TopBar } from './TopBar.js';
import { ScrollToTop } from '../ScrollToTop.js';
import { useAuth } from '../../context/AuthContext.js';

interface MainLayoutProps {
  children: React.ReactNode;
}

interface StoredUser {
  tenant: string;
  username: string;
  roles: string[];
}

const DEFAULT_USER: StoredUser = { tenant: '', username: '', roles: [] };

// Safely parse the user object from localStorage. A corrupted/invalid value
// previously threw inside render and crashed the entire app shell (white screen).
const getStoredUser = (): StoredUser => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return DEFAULT_USER;
    return { ...DEFAULT_USER, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_USER;
  }
};

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(true);
  const location = useLocation();
  const { user: authUser } = useAuth();

  // Prefer AuthContext user (reactive) with localStorage fallback
  const storedUser = getStoredUser();
  const user: StoredUser = authUser
    ? { tenant: authUser.tenant || '', username: authUser.username || '', roles: authUser.roles || [] }
    : storedUser;
  const isCoreDashboard = location.pathname.startsWith('/analyst') || 
                         location.pathname.startsWith('/executive');

  return (
    <div className="h-screen overflow-hidden bg-[#0F172A]">
      {/* Top Navigation Bar */}
      <TopBar 
        tenant={user.tenant}
        username={user.username}
        onMenuClick={() => setIsSideNavOpen(!isSideNavOpen)}
        showRoleSwitcher={isCoreDashboard}
      />

      <div className="flex h-[100vh]">
        {/* Mobile Overlay - Only shows on mobile (lg:hidden) when sidebar is open */}
        {isSideNavOpen && (
          <div 
            className="fixed inset-0 top-16 bg-black/50 z-30 lg:hidden"
            onClick={() => setIsSideNavOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Side Navigation */}
        <SideNav isOpen={isSideNavOpen} />

        {/* Main Content - Only this section scrolls */}
        <main className={`flex-1 overflow-y-auto mt-16 overflow-x-hidden p-6 transition-all duration-300 ${
          isSideNavOpen ? 'lg:ml-64' : 'lg:ml-0'
        }`}>
          <ScrollToTop />
          {children}
        </main>
      </div>
    </div>
  );
};
